import React from "react";
import { Box } from "@mui/material";
import useStore from "../../Store/Store";
import LoadingStatusBar from "./LoadingStatusBar";

/**
 * Global loading status bar that shows onboarding progress
 * regardless of which tab is selected or if source doc is open
 */
const GlobalLoadingStatusBar: React.FC = () => {
  const {
    indexingProgress,
    repoTree,
    usecaseFeatures,
    onboardingSuggestedFiles,
    triggerOnboardingWorkflow,
    currentRepo,
    onboardingWorkflowState,
    simulations,
    downloadProgress,
  } = useStore((state) => ({
    indexingProgress: state.indexingProgress,
    repoTree: state.repoTree,
    usecaseFeatures: state.usecaseFeatures,
    onboardingSuggestedFiles: state.onboardingSuggestedFiles,
    triggerOnboardingWorkflow: state.triggerOnboardingWorkflow,
    currentRepo: state.currentRepo,
    onboardingWorkflowState: state.onboardingWorkflowState,
    simulations: state.simulationsState.simulations,
    downloadProgress: state.downloadProgress,
  }));

  // Check if there are any existing simulations (AI-generated or manual)
  const hasExistingSimulations = Object.keys(simulations).length > 0;

  // Basic setup is complete when step 1 is done
  const isBasicSetupComplete = onboardingWorkflowState.step1.isCompleted;

  // AI workflow should run if there are no existing simulations OR if manually triggered
  const shouldRunAIWorkflow =
    !hasExistingSimulations || triggerOnboardingWorkflow;

  // Determine if onboarding workflow is currently in progress
  const isOnboardingRunning =
    currentRepo &&
    (downloadProgress.isDownloading ||
      triggerOnboardingWorkflow ||
      (onboardingWorkflowState.step1.isStarted &&
        !onboardingWorkflowState.step1.isCompleted) ||
      (shouldRunAIWorkflow &&
        onboardingWorkflowState.step2.isStarted &&
        !onboardingWorkflowState.step2.isCompleted) ||
      (shouldRunAIWorkflow &&
        onboardingWorkflowState.step3.isStarted &&
        !onboardingWorkflowState.step3.isCompleted));

  // Don't show if not running
  if (!isOnboardingRunning) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bgcolor="rgba(255, 255, 255, 0.3)"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      zIndex={1300} // Above most UI elements but below simulation overlay
      sx={{
        backdropFilter: "blur(6px)",
        pointerEvents: "all", // Prevent interaction with underlying elements
      }}
    >
      <Box
        width="90%"
        maxWidth={700}
        sx={{
          bgcolor: "rgba(255, 255, 255, 0.95)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          backdropFilter: "blur(10px)",
          p: 0,
        }}
      >
        <LoadingStatusBar
          indexingProgress={indexingProgress}
          repoTree={repoTree}
          suggestedFiles={onboardingSuggestedFiles}
          usecaseFeatures={usecaseFeatures}
        />
      </Box>
    </Box>
  );
};

export default GlobalLoadingStatusBar;
